import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button } from '@dorixona/ui'
import { formatDayTime, formatMoney, formatPhone } from '@dorixona/utils'
import { openOutside } from '../../app/telegram'
import { BackButton } from '../../ui/BackButton'
import { Icon } from '../../ui/Icon'
import { Loading, failureText } from '../../ui/Load'
import type { Translate } from '../../ui/translate'
import { storePoint, useOrder, useRepeatOrder, type Delivery } from './api'
import { courierStatusText, isOrderClosed, reservationOf, statusText } from './model'
import { num, useSettings } from '../../app/settings'
import { payLabel } from '../checkout/model'
import type { Store } from '../stores/api'
import { hoursText } from '../stores/model'
import { RouteLink } from '../stores/RouteLink'
import type { PayMethod } from '../checkout/api'
import { OrderReview } from '../reviews'
import { CourierTrack } from './CourierTrack'
import { usePoints } from '../../ui/points'

export function OrderScreen() {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation(['account', 'common', 'stores'])
  const { formatPoints } = usePoints()
  const order = useOrder(Number(orderId))
  const repeat = useRepeatOrder()
  const settings = useSettings()

  if (order.isPending) {
    return (
      <section className="screen order-wrap">
        <Loading what={t('common:state.loading')} count={4} />
      </section>
    )
  }

  if (order.isError) {
    return (
      <section className="screen order-wrap">
        <div className="card empty">
          <Icon name="i-box" size={54} />
          <div className="t">{failureText(order.error, t)}</div>
          <div style={{ marginTop: 18 }}>
            <Button
              onPress={() => {
                void navigate('/orders')
              }}
            >
              {t('account:order.back')}
            </Button>
          </div>
        </div>
      </section>
    )
  }

  const item = order.data
  const delivery = item.ship === 'delivery'
  const count = item.lines.reduce((sum, line) => sum + line.qty, 0)
  const reservation = reservationOf(item, num(settings.data, 'pickup.reservation_hours', 0))

  return (
    <section className="screen order-wrap">
      <div className="mhead">
        <BackButton to="/orders" />
        <h1>{t('account:orderNo', { number: item.number })}</h1>
      </div>

      <div className="od-status">
        <span className={isOrderClosed(item.status) ? 'pill stdone' : 'pill st'}>
          {statusText(item.status, t)}
        </span>
        <span className="num">{formatDayTime(item.createdAt)}</span>
      </div>

      {item.checkoutUrl ? (
        <div className="card od-pay" role="status">
          <div className="t1">{t('account:order.awaitingPay')}</div>
          <div className="t2">{t('account:order.payHint')}</div>
          <Button
            onPress={() => {
              openOutside(item.checkoutUrl)
            }}
          >
            {t('account:order.payNow', { sum: formatMoney(item.total) })}
          </Button>
        </div>
      ) : null}

      {reservation ? (
        <div className="rx-note" style={{ marginBottom: 16 }}>
          <Icon name="i-info" size={18} />
          {'at' in reservation
            ? t('account:order.pickupUntil', { date: formatDayTime(reservation.at) })
            : t('account:order.reserveAfter', { hours: reservation.hours })}
        </div>
      ) : null}

      <div className="od-grid">
        <div className="od-main">
          {item.delivery ? (
            <CourierTrack
              delivery={item.delivery}
              pharmacy={storePoint(item.store)}
              dest={item.dest}
            />
          ) : null}
          <div className="pp-card">
            <h2>
              {t('account:order.lines')}
              <span className="od-cnt num">{t('catalog:goods', { count })}</span>
            </h2>
            <div className="od-items">
              {item.lines.map((line) => (
                <div key={line.id} className="od-item">
                  <div className="cimg">
                    <Icon name="i-capsule" size={30} />
                  </div>
                  <div className="od-in">
                    <button
                      type="button"
                      className="n"
                      style={{ textAlign: 'left', color: 'var(--brand)' }}
                      onClick={() => {
                        void navigate(`/catalog/${line.productId}`)
                      }}
                    >
                      {line.name}
                    </button>
                  </div>
                  <div className="od-q num">
                    {line.qty} × {formatMoney(line.price)}
                  </div>
                  <b className="od-s num">{formatMoney(line.total)}</b>
                </div>
              ))}
            </div>
          </div>

          <div className="pp-card">
            <h2>{t(delivery ? 'common:ship.delivery' : 'common:ship.pickup')}</h2>
            <div className="spec">
              <div className="spec-row">
                <span>{t('account:order.method')}</span>
                <b>{t(delivery ? 'account:order.courier' : 'account:order.fromPharmacy')}</b>
              </div>
              {delivery ? (
                <div className="spec-row">
                  <span>{t('account:order.address')}</span>
                  <b>{item.address || t('account:order.noAddress')}</b>
                </div>
              ) : (
                <>
                  <div className="spec-row">
                    <span>{t('account:order.pharmacy')}</span>
                    <b>{item.storeName || t('account:order.choosing')}</b>
                  </div>
                  {reservation && 'at' in reservation ? (
                    <div className="spec-row">
                      <span>{t('account:order.reservedUntil')}</span>
                      <b className="num">{formatDayTime(reservation.at)}</b>
                    </div>
                  ) : null}
                  {item.store ? (
                    <>
                      <div className="spec-row">
                        <span>{t('account:order.address')}</span>
                        <b>
                          {item.store.address}
                          {item.store.metro
                            ? ` · ${t('stores:metro', { name: item.store.metro })}`
                            : ''}
                        </b>
                      </div>
                      <div className="spec-row">
                        <span>{t('stores:mode')}</span>
                        <b>{hoursText(item.store, t)}</b>
                      </div>
                    </>
                  ) : null}
                </>
              )}
              <div className="spec-row">
                <span>{t('account:order.payment')}</span>
                <b>{item.payMethod ? payLabel(item.payMethod as PayMethod, t) : '—'}</b>
              </div>
              {item.payStatus === 'held' || item.payStatus === 'released' ? (
                <div className="spec-row">
                  <span>{t('account:order.payState.label')}</span>
                  <b>{t(`account:order.payState.${item.payStatus}`)}</b>
                </div>
              ) : null}
              {item.delivery ? <CourierRows delivery={item.delivery} t={t} /> : null}
            </div>
            {!delivery && item.store ? (
              <PharmacyContacts
                store={item.store}
                route={!isOrderClosed(item.status) && item.status !== 'cancelled'}
              />
            ) : null}
          </div>

          {item.events.length > 0 ? (
            <div className="pp-card">
              <h2>{t('account:order.timeline')}</h2>
              <div className="od-items">
                {item.events.map((event) => (
                  <div key={event.id} className="spec-row">
                    <span>{event.message}</span>
                    <b className="num">{formatDayTime(event.at)}</b>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>

        <div className="od-side">
          <div className="card totals">
            <div className="tr">
              <span>{t('account:order.goods')}</span>
              <b className="num">{formatMoney(item.goods)}</b>
            </div>
            <div className="tr">
              <span>{t(delivery ? 'common:ship.delivery' : 'common:ship.pickup')}</span>
              <b className="num">
                {item.deliveryFee ? formatMoney(item.deliveryFee) : t('common:money.free')}
              </b>
            </div>
            {item.spend ? (
              <div className="tr">
                <span>{t('account:order.paidPoints')}</span>
                <b className="num" style={{ color: 'var(--brand)' }}>
                  −{formatMoney(item.spend)}
                </b>
              </div>
            ) : null}
            <div className="tr">
              <span>{t('account:order.cashback')}</span>
              <b className="num" style={{ color: 'var(--gold)' }}>
                +{formatPoints(item.cashback)}
              </b>
            </div>
            <div className="tr grand">
              <span>{t('account:order.total')}</span>
              <span className="num">{formatMoney(item.total)}</span>
            </div>
          </div>

          <Button
            disabled={repeat.isPending}
            onPress={() => {
              // Что ещё продаётся, решает сервер: он и кладёт это в корзину (R-48).
              repeat.mutate(item.id, {
                onSuccess: () => {
                  void navigate('/cart')
                },
              })
            }}
          >
            {repeat.isPending ? t('account:order.repeating') : t('account:order.repeat')}
          </Button>

          <OrderReview order={item} />
        </div>
      </div>
    </section>
  )
}

/**
 * Курьер от службы доставки в сведениях о заказе: статус и номер заявки (R-44).
 * Машина, водитель, звонок и карта — в карточке «Где курьер» выше.
 */
function CourierRows({ delivery, t }: { delivery: Delivery; t: Translate }) {
  return (
    <>
      <div className="spec-row">
        <span>{t('account:courier.title')}</span>
        <b>{courierStatusText(delivery.status, t)}</b>
      </div>
      {delivery.tracking ? (
        <div className="spec-row">
          <span>{t('account:courier.tracking')}</span>
          <b className="num">{delivery.tracking}</b>
        </div>
      ) : null}
    </>
  )
}

/**
 * Самовывоз: позвонить в аптеку и маршрут до неё — в картах телефона, если
 * открыто в нашем приложении. Телефон и точка — из карточки аптеки в самом
 * заказе. Маршрут — пока заказ ждёт покупателя; позвонить можно и после, с
 * вопросом о заказе.
 */
function PharmacyContacts({ store, route }: { store: Store; route: boolean }) {
  const { t } = useTranslation(['stores', 'account'])
  if (!store.phone && !route) return null
  return (
    <div className="od-courier od-actions">
      {store.phone ? (
        <a
          className="sc-call num"
          href={`tel:+${store.phone.replace(/\D/g, '')}`}
          aria-label={t('account:order.callPharmacy', { name: store.name })}
        >
          <Icon name="i-phone" size={15} />
          {formatPhone(store.phone)}
        </a>
      ) : null}
      {route ? (
        <RouteLink
          store={store}
          className="sc-call"
          label={t('stores:routeLabel', { name: store.name })}
        >
          <Icon name="i-pin" size={15} />
          {t('stores:route')}
        </RouteLink>
      ) : null}
    </div>
  )
}
